"use client";

import { useState } from "react";
import { Plus, Minus } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
const fabricTypes = [{
  id: 1,
  name: "MiniRoos (Ages 5-11)",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/miniroos_action.png",
  description: "The MiniRoos program at WT Birkalla is designed to introduce young players to the beautiful game in a fun, pressure-free environment. Our curriculum focuses on fundamental ball skills, coordination, and fostering a lifelong love for football. Through small-sided games, children gain more touches on the ball, ensuring every participant is engaged and developing their confidence on the pitch under the guidance of our FA-certified junior coaches."
}, {
  id: 2,
  name: "Skill Acquisition Phase (Ages 9-12)",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/sap_training.png",
  description: "Our SAP program is the foundation of technical excellence. We focus on the four core skills: first touch, running with the ball, 1v1 situations, and striking the ball. This intensive pathway prepares players for the transition into competitive youth football by refining their technical proficiency and tactical awareness. WT Birkalla provides a structured environment where high-potential athletes can thrive and master the technical requirements of the modern game."
}, {
  id: 3,
  name: "Junior Premier League (Ages 13-17)",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654193/img/jpl_match.png",
  description: "The JPL pathway represents the pinnacle of youth development at WT Birkalla. Players are immersed in a high-performance culture that emphasizes tactical discipline, physical conditioning, and mental resilience. Our JPL teams compete at the highest state levels, providing a direct bridge to our Senior First Team. With advanced video analysis and specialized position coaching, we ensure our athletes are prepared for the demands of elite-level senior football."
}];
export default function OurMaterialStandards() {
  const [openItems, setOpenItems] = useState<string[]>([]);
  const toggleItem = (id: string) => {
    setOpenItems(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };
  return <section data-section-id="242851" id="youth-development-pathways" className="bg-background text-foreground py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {}
        <div className="mb-12 text-center">
          <h2 className="font-default text-4xl sm:text-5xl font-extrabold mb-4 tracking-tighter uppercase text-primary">
            Youth Development Pathways
          </h2>
          <p className="font-default text-lg text-foreground/90 max-w-2xl mx-auto">
            WT Birkalla is committed to nurturing the next generation of football talent through our structured elite development programs, from first kicks to senior debuts.
          </p>
        </div>

        {}
        <Accordion type="multiple" value={openItems} onValueChange={setOpenItems} className="w-full">
          {fabricTypes.map((fabric, index) => <AccordionItem key={fabric.id} value={fabric.id.toString()} data-index={index} className="border-b border-primary/20 last:border-b-0 bg-card/50 backdrop-blur-sm mb-4 rounded-lg overflow-hidden">
              <AccordionTrigger className="py-6 px-4 hover:bg-primary/5 transition-colors duration-200 flex items-center justify-between gap-4 group" onClick={() => toggleItem(fabric.id.toString())}>
                <div className="flex items-center gap-6">
                  <span className="font-mono text-4xl font-bold text-transparent stroke-primary stroke-1 opacity-30 group-hover:opacity-100 transition-opacity" style={{
                WebkitTextStroke: '1px hsl(45, 85%, 55%)'
              }}>
                    0{fabric.id}
                  </span>
                  <span className="font-default text-xl sm:text-2xl font-bold text-left uppercase tracking-tight text-foreground">
                    {fabric.name}
                  </span>
                </div>
                <div className="text-primary flex-shrink-0">
                  {openItems.includes(fabric.id.toString()) ? <Minus className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
                </div>
              </AccordionTrigger>

              <AccordionContent className="px-4 pb-6">
                <div className="flex flex-col sm:flex-row gap-6">
                  {}
                  <div className="w-full sm:w-48 flex-shrink-0">
                    <img src={fabric.image} data-wvc-srcset={`${fabric.image}-wvc-srcset`} data-wvc-sizes={`${fabric.image}-wvc-sizes`} alt={`WT Birkalla ${fabric.name} program`} loading="lazy" decoding="async" className="w-full h-48 sm:h-40 object-cover border-t-2 border-primary rounded-sm grayscale hover:grayscale-0 transition-all duration-500" />
                  </div>

                  {}
                  <div className="flex-1">
                    <h4 className="font-default text-primary font-bold uppercase text-sm mb-2 tracking-widest">Program Details</h4>
                    <p className="font-default text-sm sm:text-base text-foreground/80 leading-relaxed">
                      {fabric.description}
                    </p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>)}
        </Accordion>

        {}
        <div className="mt-12 text-center">
          <p className="font-default text-foreground/70 mb-6 italic">
            Building the future of South Australian football since 1933. Join the Birkalla legacy.
          </p>
        </div>
      </div>
    </section>;
}